import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, Card, Group, MultiSelect, PasswordInput, SegmentedControl, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { createOne } from '@/shared/api/jsonapi/client';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { passwordSchema } from '@/features/auth/password-utils';
import { md5 } from '@/shared/lib/md5';
import { createOrAssociateUser, type CreateUserOutcome } from '@/features/users/api';
import { fetchRoles } from '@/features/roles/api';

export const Route = createFileRoute('/_app/users/new')({ component: NewUserPage });

type Mode = 'invite' | 'manual';

function NewUserPage() {
  const [mode, setMode] = useState<Mode>('invite');

  return (
    <Stack>
      <Title order={1}>Nuovo utente</Title>
      <SegmentedControl
        value={mode}
        onChange={(v) => setMode(v as Mode)}
        maw={620}
        data={[
          { label: 'Invita via email', value: 'invite' },
          { label: 'Crea con password', value: 'manual' },
        ]}
      />
      {mode === 'invite' ? <InviteForm /> : <ManualForm />}
    </Stack>
  );
}

// ─── Modalità 1: invito/associazione via workflow POST /api/user-profiles ────
// Nessuna password: il BE assegna quella di default e invia un'email di benvenuto/OTP.
// Se l'utente esiste già viene associato/invitato a questo tenant.

const inviteSchema = z.object({
  email: z.string().email('Email non valida'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  roleIds: z.array(z.string()),
});

/** Colore notifica: ok pieno = verde, ok parziale (email KO) = arancio, errore = rosso. */
function outcomeColor(o: CreateUserOutcome): string {
  if (!o.ok) return 'red';
  return o.kind === 'emailFailed' ? 'orange' : 'green';
}

function InviteForm() {
  const navigate = useNavigate();
  const form = useForm({
    mode: 'controlled',
    initialValues: { email: '', firstName: '', lastName: '', roleIds: [] as string[] },
    validate: zodResolver(inviteSchema),
  });

  // I ruoli sono quelli del tenant corrente (o del sottotenant, se cross-tenant attivo):
  // fetchRoles passa per il client JSON:API che inietta il filtro tenant.
  const rolesQ = useQuery({ queryKey: ['roles'], queryFn: fetchRoles });
  const roleOptions = (rolesQ.data?.data ?? []).map((r) => ({
    value: r.id,
    label: r.isFromMaster ? `${r.name ?? r.id} (sistema)` : (r.name ?? r.id),
  }));

  const create = useMutation({
    mutationFn: (v: typeof form.values) =>
      createOrAssociateUser({ email: v.email, firstName: v.firstName, lastName: v.lastName, roleIds: v.roleIds }),
    onSuccess: (outcome) => {
      notifications.show({
        color: outcomeColor(outcome),
        title: outcome.ok ? 'Operazione completata' : 'Operazione non riuscita',
        message: outcome.message,
      });
      if (outcome.ok) void navigate({ to: '/users' });
    },
    onError: (err) => {
      notifications.show({ color: 'red', title: 'Operazione fallita', message: err instanceof Error ? err.message : 'Errore di rete' });
    },
  });

  const onSubmit = form.onSubmit((v) => void create.mutate(v));

  return (
    <Card withBorder padding="lg" radius="md" maw={620}>
      <Text c="dimmed" size="sm" mb="md">
        Inserisci l&apos;email dell&apos;utente. Se non esiste viene creato e riceve un&apos;email di benvenuto;
        se esiste già viene invitato ad accedere a questo tenant. I ruoli sono opzionali (default: «User»).
      </Text>
      <form onSubmit={onSubmit}>
        <Stack>
          <TextInput label="Email" required type="email" placeholder="nome@azienda.it" {...form.getInputProps('email')} />
          <Group grow>
            <TextInput label="Nome" {...form.getInputProps('firstName')} />
            <TextInput label="Cognome" {...form.getInputProps('lastName')} />
          </Group>
          <MultiSelect
            label="Ruoli"
            description="Lascia vuoto per assegnare il ruolo base «User»."
            placeholder={roleOptions.length ? 'Seleziona uno o più ruoli' : 'Nessun ruolo disponibile'}
            data={roleOptions}
            searchable
            clearable
            disabled={rolesQ.isLoading}
            {...form.getInputProps('roleIds')}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => void navigate({ to: '/users' })}>
              Annulla
            </Button>
            <Button type="submit" loading={create.isPending}>
              Crea / Invita
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
}

// ─── Modalità 2: creazione diretta con password iniziale ─────────────────────
// Crea il record utente (AspNetUsers) con una password scelta dall'admin, senza
// flusso email. I ruoli si assegnano poi dalla scheda utente.

const manualSchema = z.object({
  email: z.string().email('Email non valida'),
  firstName: z.string().min(1, 'Obbligatorio'),
  lastName: z.string().min(1, 'Obbligatorio'),
  password: passwordSchema,
});

function ManualForm() {
  const navigate = useNavigate();
  const form = useForm({
    mode: 'controlled',
    initialValues: { email: '', firstName: '', lastName: '', password: '' },
    validate: zodResolver(manualSchema),
  });

  const create = useMutation({
    mutationFn: (v: typeof form.values) =>
      createOne('users', {
        attributes: {
          email: v.email,
          userName: v.email,
          firstName: v.firstName,
          lastName: v.lastName,
          passwordHash: md5(v.password),
          isPasswordMd5: true,
          emailConfirmed: true,
        },
      }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Utente creato' });
      void navigate({ to: '/users' });
    },
    onError: (err) => {
      const msg = err instanceof JsonApiHttpError ? (err.errors[0]?.detail ?? 'Errore') : err instanceof Error ? err.message : 'Errore';
      notifications.show({ color: 'red', title: 'Creazione fallita', message: msg });
    },
  });

  const onSubmit = form.onSubmit((v) => void create.mutate(v));

  return (
    <Card withBorder padding="lg" radius="md" maw={620}>
      <Text c="dimmed" size="sm" mb="md">
        Crea direttamente l&apos;utente con una password iniziale, senza email di invito. Assegna i ruoli in seguito
        dalla scheda dell&apos;utente.
      </Text>
      <form onSubmit={onSubmit}>
        <Stack>
          <TextInput label="Email" required type="email" {...form.getInputProps('email')} />
          <Group grow>
            <TextInput label="Nome" required {...form.getInputProps('firstName')} />
            <TextInput label="Cognome" required {...form.getInputProps('lastName')} />
          </Group>
          <PasswordInput label="Password iniziale" required {...form.getInputProps('password')} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => void navigate({ to: '/users' })}>
              Annulla
            </Button>
            <Button type="submit" loading={create.isPending}>
              Crea
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
}
